// Box detail panel — expanded view with health history sparkline

import { clsx } from 'clsx'
import { X } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/loading'
import { StatusBadge } from '@/components/ui/status-badge'
import { useBoxDetail } from '@/hooks/use-boxes'
import type { BoxHealthPoint } from '@/lib/types-boxes'

interface BoxDetailPanelProps {
  boxId: string
  onClose: () => void
}

function formatRate(rate: string | number): string {
  const num = typeof rate === 'string' ? Number.parseFloat(rate) : rate
  return `${(num * 100).toFixed(2)}%`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function HealthSparkline({ data }: { data: BoxHealthPoint[] }): React.JSX.Element {
  // Show data points as CSS bar chart, newest on right
  const points = [...data].reverse()
  const maxScore = 100

  return (
    <div className="flex items-end gap-px" style={{ height: 48 }}>
      {points.map((point) => {
        const height = Math.max(2, (point.health_score / maxScore) * 48)
        const color =
          point.health_score >= 80
            ? 'bg-green-500'
            : point.health_score >= 50
              ? 'bg-yellow-500'
              : point.health_score >= 20
                ? 'bg-orange-500'
                : 'bg-red-500'
        return (
          <div
            key={point.id}
            className={clsx('w-2 rounded-t', color)}
            style={{ height }}
            title={`${point.health_score}/100 - ${formatDate(point.recorded_at)}`}
          />
        )
      })}
    </div>
  )
}

function VolumeSparkline({ data }: { data: BoxHealthPoint[] }): React.JSX.Element {
  const points = [...data].reverse()
  const maxVol = Math.max(...points.map((p) => p.volume), 1)

  return (
    <div className="flex items-end gap-px" style={{ height: 48 }}>
      {points.map((point) => {
        const height = Math.max(2, (point.volume / maxVol) * 48)
        return (
          <div
            key={point.id}
            className="w-2 rounded-t bg-admin-400"
            style={{ height }}
            title={`${point.volume.toLocaleString()} emails - ${formatDate(point.recorded_at)}`}
          />
        )
      })}
    </div>
  )
}

function MetricRow({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-text-secondary">{label}</span>
      <div className="text-right">
        <span className="text-sm font-medium text-text-primary">{value}</span>
        {sub && <span className="ml-1.5 text-xs text-text-tertiary">{sub}</span>}
      </div>
    </div>
  )
}

export function BoxDetailPanel({ boxId, onClose }: BoxDetailPanelProps): React.JSX.Element {
  const { data, isLoading } = useBoxDetail(boxId)

  if (isLoading || !data) {
    return (
      <div className="fixed inset-y-0 right-0 z-40 w-96 border-l border-surface-3 bg-surface-0 shadow-xl">
        <LoadingSpinner className="mt-24" />
      </div>
    )
  }

  const { box, history } = data

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-96 overflow-y-auto border-l border-surface-3 bg-surface-0 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-3 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-text-primary">{box.hostname}</h2>
          <StatusBadge status={box.status} />
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-1 hover:bg-surface-2">
          <X className="h-5 w-5 text-text-tertiary" />
        </button>
      </div>

      <div className="space-y-5 p-5">
        {/* Health history */}
        <div>
          <h3 className="mb-2 text-sm font-medium text-text-primary">
            Health Score (last 30 checks)
          </h3>
          {history.length > 0 ? (
            <HealthSparkline data={history} />
          ) : (
            <p className="text-xs text-text-tertiary">No health history yet</p>
          )}
        </div>

        {/* Volume history */}
        <div>
          <h3 className="mb-2 text-sm font-medium text-text-primary">Volume</h3>
          {history.length > 0 ? (
            <VolumeSparkline data={history} />
          ) : (
            <p className="text-xs text-text-tertiary">No volume history yet</p>
          )}
        </div>

        {/* Metrics */}
        <div className="divide-y divide-surface-3 rounded-lg border border-surface-3 px-4">
          <MetricRow label="Health Score" value={`${box.health_score}/100`} />
          <MetricRow
            label="Weight"
            value={`${box.weight}%`}
            sub={`target: ${box.target_weight}%`}
          />
          <MetricRow label="Volume (24h)" value={box.volume_24h.toLocaleString()} />
          <MetricRow label="Bounce Rate" value={formatRate(box.bounce_rate_24h)} />
          <MetricRow label="Complaint Rate" value={formatRate(box.complaint_rate_24h)} />
          <MetricRow label="Deferral Rate" value={formatRate(box.deferral_rate_1h)} />
          <MetricRow label="Success Rate (5m)" value={formatRate(box.send_success_rate_5m)} />
          <MetricRow label="p99 Latency" value={`${box.p99_latency_ms}ms`} />
          <MetricRow label="Spamhaus" value={box.spamhaus_clean ? 'Clean' : 'LISTED'} />
        </div>

        {/* Warming progress */}
        {box.status === 'warming' && (
          <div>
            <h3 className="mb-2 text-sm font-medium text-text-primary">Warming Progress</h3>
            <div className="h-3 w-full rounded-full bg-surface-2">
              <div
                className="h-3 rounded-full bg-admin-500 transition-all"
                style={{ width: `${Math.min(100, (box.warming_day / 30) * 100)}%` }}
              />
            </div>
            <div className="mt-1 flex justify-between text-xs text-text-tertiary">
              <span>Day {box.warming_day}/30</span>
              <span>Started: {formatDate(box.warming_started_at)}</span>
            </div>
          </div>
        )}

        {/* Configuration */}
        <div>
          <h3 className="mb-2 text-sm font-medium text-text-primary">Configuration</h3>
          <div className="space-y-1 text-xs">
            <p className="text-text-secondary">
              URL: <span className="text-text-primary">{box.url}</span>
            </p>
            <p className="text-text-secondary">
              IP: <span className="text-text-primary">{box.ip_address}</span>
            </p>
            <p className="text-text-secondary">
              Pool: <span className="text-text-primary capitalize">{box.pool}</span>
            </p>
            <p className="text-text-secondary">
              Last health check:{' '}
              <span className="text-text-primary">{formatDate(box.last_health_check)}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
